import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, ArrowRight } from "lucide-react";
import api from "@/lib/api";
import NewPeriodModal from "@/components/NewPeriodModal";
import { formatKina } from "@/lib/money";
import { PAYRUN } from "@/constants/testIds";
import { useAuth } from "@/context/AuthContext";

const STATUS_STYLE = {
    draft: { bg: "#FFF6E6", fg: "#5A3600", label: "Draft" },
    approved: { bg: "#E6F4EC", fg: "#0A6A2E", label: "Approved" },
    locked: { bg: "#2A2A2C", fg: "#fff", label: "Locked" },
};

export default function PayRunList() {
    const { user } = useAuth();
    const navigate = useNavigate();
    const [periods, setPeriods] = useState([]);
    const [showNew, setShowNew] = useState(false);
    const isAdmin = user?.role === "admin";

    const load = async () => {
        const { data } = await api.get("/pay-periods");
        setPeriods(data);
    };
    useEffect(() => { load(); }, []);

    const draft = periods.find((p) => p.status === "draft");

    return (
        <div data-testid={PAYRUN.listPage} className="p-4 sm:p-6 md:p-10 max-w-[1200px]">
            <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between mb-6 md:mb-8 gap-4">
                <div className="min-w-0">
                    <div className="text-[11px] uppercase tracking-[0.24em] text-neutral-500 font-display">Pay runs</div>
                    <h1 className="font-display font-bold text-2xl sm:text-3xl md:text-4xl mt-1 text-[color:var(--kp-charcoal)] break-words">
                        {periods.length ? `${periods.length} pay period${periods.length === 1 ? "" : "s"}` : "No pay periods yet"}
                    </h1>
                    <p className="text-sm text-neutral-500 mt-1">
                        Fortnightly runs, one PPE per Tuesday. Approve to lock, export payslips + bank file + IRC remittance.
                    </p>
                </div>
                {isAdmin && (
                    draft ? (
                        <button
                            onClick={() => navigate(`/pay-runs/${draft.id}`)}
                            className="inline-flex items-center gap-2 px-4 py-2.5 text-[13px] font-display font-semibold uppercase tracking-[0.12em] text-white rounded-[3px] shrink-0"
                            style={{ background: "var(--kp-red)" }}
                        >
                            Continue draft → <ArrowRight size={15} />
                        </button>
                    ) : (
                        <button
                            onClick={() => setShowNew(true)}
                            className="inline-flex items-center gap-2 px-4 py-2.5 text-[13px] font-display font-semibold uppercase tracking-[0.12em] text-white rounded-[3px] shrink-0"
                            style={{ background: "var(--kp-red)" }}
                        >
                            <Plus size={16} strokeWidth={2.2} /> New pay period
                        </button>
                    )
                )}
            </div>

            {periods.length === 0 ? (
                <div className="bg-white border border-dashed border-neutral-300 p-12 text-center rounded-[3px]">
                    <div className="font-display font-bold text-xl text-[color:var(--kp-charcoal)] mb-1">No runs yet</div>
                    <div className="text-sm text-neutral-500">Start the first fortnight to build the master list history.</div>
                </div>
            ) : (
                <div className="bg-white border border-neutral-200 rounded-[3px] overflow-x-auto">
                    <table className="w-full text-sm min-w-[640px]">
                        <thead>
                            <tr className="text-left text-[10px] uppercase tracking-[0.16em] text-neutral-500 font-display" style={{ background: "#FAFAFA" }}>
                                <th className="px-5 py-3">PPE</th>
                                <th className="px-5 py-3">Start → End</th>
                                <th className="px-5 py-3">Status</th>
                                <th className="px-5 py-3 text-right">Gross</th>
                                <th className="px-5 py-3 text-right">Tax</th>
                                <th className="px-5 py-3 text-right">Net</th>
                            </tr>
                        </thead>
                        <tbody>
                            {periods.map((p) => {
                                const s = STATUS_STYLE[p.status] || STATUS_STYLE.draft;
                                return (
                                    <tr
                                        key={p.id}
                                        data-testid={PAYRUN.listRow(p.id)}
                                        className="border-t border-neutral-100 hover:bg-neutral-50 cursor-pointer"
                                        onClick={() => navigate(p.status === "draft" ? `/pay-runs/${p.id}` : `/pay-runs/${p.id}/complete`)}
                                    >
                                        <td className="px-5 py-3 font-mono-money text-[color:var(--kp-charcoal)] font-medium">
                                            {p.ppe_date}
                                            {p.biometric_pending_submission?.submitted_by && (
                                                <span
                                                    data-testid={`payrun-pending-chip-${p.id}`}
                                                    className="ml-2 inline-block px-1.5 py-0.5 text-[9px] uppercase tracking-[0.14em] font-display font-semibold rounded-[3px]"
                                                    style={{ background: "#FFF8E6", color: "#8A6D00", border: "1px solid #E6D389" }}
                                                    title={`Biometric upload from ${p.biometric_pending_submission.submitted_by} pending review`}
                                                >
                                                    Pending bio
                                                </span>
                                            )}
                                        </td>
                                        <td className="px-5 py-3 text-neutral-500 font-mono-money text-xs">
                                            {p.start_date} → {p.end_date}
                                        </td>
                                        <td className="px-5 py-3">
                                            <span className="inline-block px-2 py-0.5 text-[10px] uppercase tracking-[0.14em] font-display font-semibold rounded-[3px]" style={{ background: s.bg, color: s.fg }}>
                                                {s.label}
                                            </span>
                                        </td>
                                        <td className="px-5 py-3 text-right font-mono-money">{formatKina(p.totals?.gross)}</td>
                                        <td className="px-5 py-3 text-right font-mono-money" style={{ color: "var(--kp-red)" }}>{formatKina(p.totals?.tax)}</td>
                                        <td className="px-5 py-3 text-right font-mono-money" style={{ color: "var(--kp-success)" }}>{formatKina(p.totals?.net)}</td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}

            <NewPeriodModal
                open={showNew}
                onClose={() => setShowNew(false)}
                onCreated={(p) => { setShowNew(false); navigate(`/pay-runs/${p.id}`); }}
            />
        </div>
    );
}
